import React from "react";
import { Booking } from "../../services/bookingService";
import { BOOKING_STATUS, BookingStatus } from "../../constants/bookingStatus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faClock,
  faMapMarkerAlt,
  faCar,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

interface BookingCardProps {
  booking: Booking;
  onClick: () => void;
}

const BookingCard: React.FC<BookingCardProps> = ({ booking, onClick }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatusChip = (status: BookingStatus) => {
    const baseClasses = "px-3 py-1 text-xs font-semibold rounded-full";
    switch (status) {
      case BOOKING_STATUS.CONFIRMED:
      case BOOKING_STATUS.ASSIGNED:
        return `bg-green-100 text-green-800 ${baseClasses}`;
      case BOOKING_STATUS.COMPLETED:
        return `bg-blue-100 text-blue-800 ${baseClasses}`;
      case BOOKING_STATUS.CANCELLED:
        return `bg-red-100 text-red-800 ${baseClasses}`;
      case BOOKING_STATUS.PENDING:
      case BOOKING_STATUS.ONGOING:
        return `bg-yellow-100 text-yellow-800 ${baseClasses}`;
      default:
        return `bg-gray-100 text-gray-800 ${baseClasses}`;
    }
  };

  return (
    <div
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer border border-gray-200 overflow-hidden"
      onClick={onClick}
    >
      <div className="p-5">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-sm text-gray-500">Booking ID</p>
            <p className="font-semibold text-gray-800 tracking-wider">
              {booking.booking_reference}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={getStatusChip(booking.booking_status)}>
              {booking.booking_status}
            </span>
            {booking.booking_status === BOOKING_STATUS.COMPLETED &&
              booking.driver_id &&
              !booking.feedback_submitted && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                  Rate Driver
                </span>
              )}
          </div>
        </div>

        <div className="my-5 border-t border-gray-200" />

        <div className="flex items-center text-gray-700">
          <FontAwesomeIcon
            icon={faMapMarkerAlt}
            className="mr-3 text-gray-400"
          />
          <span className="font-semibold">
            {booking.SourceBooking?.name || booking.source}
          </span>
          <FontAwesomeIcon icon={faArrowRight} className="mx-4 text-gray-400" />
          <span className="font-semibold">
            {booking.DestinationBooking?.name || booking.destination}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mt-5 text-sm">
          <div className="flex items-center text-gray-600">
            <FontAwesomeIcon icon={faCar} className="mr-3 w-4 text-gray-400" />
            <span>{booking.cab_type}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="mr-3 w-4 text-gray-400"
            />
            <span>{formatDate(booking.pickup_date)}</span>
          </div>
          {booking.return_date && (
            <div className="flex items-center text-gray-600">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="mr-3 w-4 text-gray-400"
              />
              <span>Return: {formatDate(booking.return_date)}</span>
            </div>
          )}
          <div className="flex items-center text-gray-600">
            <FontAwesomeIcon
              icon={faClock}
              className="mr-3 w-4 text-gray-400"
            />
            <span>{formatTime(booking.pickup_time)}</span>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 px-5 py-3">
        <p className="text-right">
          <span className="text-gray-600">Total Fare: </span>
          <span className="font-bold text-lg text-gray-800">
            ₹{(booking.total_fare_bc || 0).toFixed(2)}
          </span>
        </p>
      </div>
    </div>
  );
};

export default BookingCard;
